import { BarChart3 } from "lucide-react";
import { CategoryHeatmap } from "./CategoryHeatmap";
import { CategoryRadarChart } from "./CategoryRadarChart";
import { KpiCards } from "./KpiCards";
import { ProfileComparison } from "./ProfileComparison";
import { RiskView } from "./RiskView";
import { ScoreDistribution } from "./ScoreDistribution";
import { useChartData } from "./useChartData";

interface CampaignChartsProps {
  campaignId: string;
  profileId: string | null;
  refreshKey: number;
}

export function CampaignCharts({ campaignId, profileId, refreshKey }: CampaignChartsProps) {
  const { data, loading, error, refetch } = useChartData(campaignId, profileId, refreshKey);

  if (loading) {
    return (
      <div className="space-y-8 p-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {["a", "b", "c", "d", "e", "f"].map((k) => (
            <div
              key={k}
              className="rounded-lg border border-border bg-card p-4 h-24 animate-pulse"
            />
          ))}
        </div>
        <div className="h-[300px] rounded-lg bg-muted animate-pulse" />
        <div className="h-[200px] rounded-lg bg-muted animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <p className="text-sm text-destructive mb-2">{error}</p>
          <button type="button" onClick={refetch} className="text-sm text-primary hover:underline">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data || data.locations.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <BarChart3 className="mx-auto h-8 w-8 mb-2 opacity-40" />
          <p className="text-sm font-medium">Score locations to see charts</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Select a scoring profile and run scoring to generate chart data.
          </p>
        </div>
      </div>
    );
  }

  const scores = data.locations.map((l) => l.totalScore);

  return (
    <div className="space-y-5 p-4">
      <KpiCards data={data} />

      <section className="rounded-lg border border-border bg-card p-5">
        <h3 className="mb-4 text-sm font-semibold">Category Breakdown</h3>
        <CategoryRadarChart locations={data.locations} profileWeights={data.profileWeights} />
      </section>

      <section className="rounded-lg border border-border bg-card p-5">
        <h3 className="mb-4 text-sm font-semibold">Score Distribution</h3>
        <ScoreDistribution scores={scores} />
      </section>

      <section className="rounded-lg border border-border bg-card p-5">
        <h3 className="mb-4 text-sm font-semibold">Category Scores by Area</h3>
        <CategoryHeatmap locations={data.locations} />
      </section>

      <section className="rounded-lg border border-border bg-card p-5">
        <h3 className="mb-4 text-sm font-semibold">Profile Comparison</h3>
        <ProfileComparison campaignId={campaignId} profileId={profileId} />
      </section>

      <section className="rounded-lg border border-border bg-card p-5">
        <h3 className="mb-4 text-sm font-semibold">Risk Analysis</h3>
        <RiskView locations={data.locations} />
      </section>
    </div>
  );
}
